import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AboutContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 2rem 4rem;
  background: var(--bg-secondary);
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease;
`;

const PageTitle = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  span {
    color: #F5B800;
  }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  background: var(--card-bg);
  padding: 3.5rem;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2.5rem;
  border-left: 4px solid #F5B800;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: var(--text-primary);
  margin-bottom: 2rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.9;
  margin-bottom: 1.5rem;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(245, 184, 0, 0.05) 0%, rgba(46, 196, 182, 0.05) 100%);
  padding: 3rem;
  border-radius: 4px;
  margin: 3rem 0;
  border-left: 4px solid #2EC4B6;
`;

const HighlightTitle = styled.h3`
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  color: var(--text-primary);
  margin-bottom: 2rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const ListItem = styled.li`
  font-size: 1.05rem;
  color: var(--text-secondary);
  padding: 1.25rem;
  padding-left: 3rem;
  position: relative;
  background: var(--card-bg);
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:before {
    content: '✓';
    position: absolute;
    left: 1rem;
    top: 1.25rem;
    color: white;
    background: #F5B800;
    width: 24px;
    height: 24px;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
  }

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 4px 15px rgba(245, 184, 0, 0.2);
    border-left: 3px solid #F5B800;
  }
`;

const CertificationBox = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

const CertCard = styled.div`
  background: linear-gradient(135deg, #0E1114 0%, #2C2F33 100%);
  padding: 3rem;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  border-top: 4px solid #F5B800;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(245, 184, 0, 0.3);
  }
`;

const CertTitle = styled.h4`
  color: #F5B800;
  margin-bottom: 1rem;
  font-size: 1.6rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
`;

const CertDetails = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.05rem;
  line-height: 1.8;
`;

const About = () => {
  return (
    <AboutContainer>
      <Container>
        <PageHeader>
          <PageTitle>About <span>Vital-Link</span></PageTitle>
          <PageSubtitle>Excellence in Large-Scale Telecommunications & Industrial Engineering</PageSubtitle>
        </PageHeader>

        <ContentSection>
          <SectionTitle>Our Story</SectionTitle>
          <Paragraph>
            Vital-Link Structured Wiring and Advanced Engineering LLC was founded on a commitment to delivering
            exceptional large-scale telecommunications and industrial network infrastructure solutions. With over 20 years of proven
            experience in the industry, we bring unparalleled expertise and dedication to every enterprise-level project.
          </Paragraph>
          <Paragraph>
            Serving telecommunications providers and industrial clients nationwide across the United States with professional
            headend and central office installations, fiber and coax auditing, PON QA testing, and advanced engineering solutions.
            Our work is characterized by meticulous attention to detail, adherence to industry standards, and
            a relentless pursuit of excellence in large-scale deployments.
          </Paragraph>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Our Leadership</SectionTitle>
          <HighlightBox>
            <HighlightTitle>Co-Founders</HighlightTitle>
            <Paragraph>
              <strong>George Sherrer</strong> - Co-Founder<br />
              <a href="mailto:george.sherrer@vital-link-cable-solutions.com" style={{color: '#2EC4B6', textDecoration: 'none'}}>george.sherrer@vital-link-cable-solutions.com</a><br />
              Strategic business leader bringing extensive experience in telecommunications operations and large-scale provider solutions.
            </Paragraph>
          </HighlightBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Our Expertise</SectionTitle>
          <Paragraph>
            As a proven System Engineer with extensive experience in telecommunications and network infrastructure,
            Vital-Link offers expertise across multiple domains:
          </Paragraph>

          <HighlightBox>
            <HighlightTitle>Core Competencies</HighlightTitle>
            <List>
              <ListItem>DOCSIS 3.0/3.1 OFDM/OFDMA implementation</ListItem>
              <ListItem>XGS-PON (10G Passive Optical Network) engineering</ListItem>
              <ListItem>PON QA testing & validation</ListItem>
              <ListItem>Industrial-scale structured cabling & cable management</ListItem>
              <ListItem>Enterprise network infrastructure design & deployment</ListItem>
              <ListItem>CMTS/CPE configuration & certification</ListItem>
              <ListItem>RF testing, validation & optimization</ListItem>
              <ListItem>IPv6 deployment & certification</ListItem>
              <ListItem>Network problem resolution</ListItem>
              <ListItem>Quality assurance testing</ListItem>
              <ListItem>Technical documentation & standards</ListItem>
            </List>
          </HighlightBox>

          <Paragraph>
            We have worked extensively with industry-leading vendors including Cisco Systems (CBR8, E6000),
            Casa Systems, Arris International, Adtran, and Gainspeed, providing installation, testing,
            turn-up, and certification services for mission-critical telecommunications equipment.
          </Paragraph>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Professional Certifications</SectionTitle>
          <Paragraph>
            Our commitment to professional excellence is demonstrated through industry-recognized certifications:
          </Paragraph>

          <CertificationBox>
            <CertCard>
              <CertTitle>DOCSIS Engineer Professional (DEP)</CertTitle>
              <CertDetails>
                Issuing Authority: SCTE, ISBE<br />
                Issued: March 2017<br />
                Status: No Expiration Date
              </CertDetails>
            </CertCard>

            <CertCard>
              <CertTitle>Fiber Installation Certification</CertTitle>
              <CertDetails>
                Issuing Authority: ETA International<br />
                Credential ID: FO1164166
              </CertDetails>
            </CertCard>
          </CertificationBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Experience & Track Record</SectionTitle>
          <Paragraph>
            With over 20 years in telecommunications, our experience spans:
          </Paragraph>
          <List>
            <ListItem>Advanced Engineering for WOW & Charter Communications</ListItem>
            <ListItem>DOCSIS QA & CPE/XGS-PON/DAA engineering</ListItem>
            <ListItem>Lead deployment & network engineering</ListItem>
            <ListItem>Project management & team leadership</ListItem>
            <ListItem>National & international implementations</ListItem>
            <ListItem>Training & knowledge transfer</ListItem>
          </List>
        </ContentSection>

        <ContentSection>
          <SectionTitle>Our Commitment</SectionTitle>
          <Paragraph>
            At Vital-Link, we are driven by strategic thinking, attention to detail, and a commitment to
            delivering results that exceed expectations. We understand that reliable network infrastructure
            is essential for large-scale telecommunications providers, and we take pride in providing industrial-grade solutions that are
            built to last.
          </Paragraph>
          <Paragraph>
            We serve exclusively large-scale telecommunications providers and enterprise clients—not residential installations. Whether you need headend and central office installations, fiber and coax auditing, PON QA testing, or
            advanced telecommunications engineering services for your industrial-scale infrastructure, Vital-Link brings the expertise, professionalism,
            and dedication needed to ensure your project's success.
          </Paragraph>
        </ContentSection>
      </Container>
    </AboutContainer>
  );
};

export default About;
